import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { jwtDecode } from "jwt-decode";

const axiosApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// Flag to prevent infinite refresh loops
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const clearAdminTokens = () => {
  localStorage.removeItem("admin_access_token");
  localStorage.removeItem("admin_refresh_token");
  localStorage.removeItem("admin_token_expires_at");
};

axiosApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("admin_access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosApi.interceptors.response.use(
  (response) => {
    if (
      response?.request?.responseURL?.includes("v1/admin/verify-token") ||
      response?.request?.responseURL?.includes("v1/admin/reset-pass") ||
      response?.request?.responseURL?.includes("v1/admin/sign-in")
    ) {
      return response;
    }
    // Only check admin token if we're on an admin page (not dealer/user pages).
    // Dealer pages are at "/" (root) and "/_*" paths; everything else is admin.
    const pathname = window.location.pathname;
    const isAdminPage = pathname !== "/" && !pathname.startsWith("/_") && pathname !== "/login";
    if (isAdminPage) {
      const user = localStorage.getItem("admin_access_token");
      if (user) {
        try {
          const jsonUser: any = jwtDecode(user);
          if (!jsonUser?.first_name || !jsonUser?.last_name) {
            clearAdminTokens();
            window.location.href = "/login";
          }
        } catch {
          clearAdminTokens();
          window.location.href = "/login";
        }
      }
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Only attempt refresh on 401 errors, and not on refresh-token or sign-in requests
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("refresh-token") &&
      !originalRequest.url?.includes("sign-in")
    ) {
      const refreshToken = localStorage.getItem("admin_refresh_token");

      if (!refreshToken) {
        clearAdminTokens();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request until refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosApi(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/admin/refresh-token`,
          { refresh_token: refreshToken }
        );

        const { access_token, refresh_token: newRefreshToken, expires_in } = response.data;

        localStorage.setItem("admin_access_token", access_token);
        localStorage.setItem("admin_refresh_token", newRefreshToken);
        localStorage.setItem("admin_token_expires_at", String(Date.now() + expires_in * 1000));

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        processQueue(null, access_token);

        return axiosApi(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAdminTokens();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // For non-401 errors or if refresh already failed.
    // Don't redirect on sign-in or refresh-token failures — let the caller handle those.
    if (
      error.response?.status === 401 &&
      !originalRequest.url?.includes("sign-in") &&
      !originalRequest.url?.includes("refresh-token")
    ) {
      clearAdminTokens();
      window.location.href = "/login";
    }

    return Promise.reject(error);
  }
);

export async function get(url: string, config = {}) {
  return await axiosApi
    .get(url, { ...config })
    .then((response) => response.data);
}

export async function post(url: string, data: {}, config = {}) {
  return await axiosApi
    .post(url, { ...data }, { ...config })
    .then((response) => response.data);
}

export async function put(url: string, data: {}, config = {}) {
  return axiosApi
    .put(url, { ...data }, { ...config })
    .then((response) => response.data);
}

export async function patch(url: string, data: {}, config = {}) {
  return axiosApi
    .patch(url, { ...data }, { ...config })
    .then((response) => response.data);
}

export async function del(url: string, config = {}) {
  return await axiosApi
    .delete(url, { ...config })
    .then((response) => response.data);
}
